import { useState } from 'react';
import { Alert, Button, Group, List, Modal, Stack, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';
import type { CompleteStockCountResponse, StockCountLineResponse } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { useActiveProducts, useCompleteStockCount, useLocations } from '../api';
import { formatQty, formatSignedQty } from '../utils';

/** "Complete" a stock count (BUILD_SPEC.md §8.6: "complete (shows the variance
 * adjustments that will post, then posts)"). The preview is computed
 * client-side from the already-loaded lines — `POST /:id/complete` has no
 * separate dry-run endpoint, it computes+posts atomically, so this is the one
 * place the exact same expected-vs-counted diff the server will act on is shown
 * to the operator before they confirm. */
export function CompleteCountButton({
  countId,
  siteId,
  lines,
}: {
  countId: string;
  siteId: string;
  lines: StockCountLineResponse[];
}) {
  const canOperate = useCanOperate();
  const [opened, setOpened] = useState(false);
  const [result, setResult] = useState<CompleteStockCountResponse | null>(null);
  const locationsQuery = useLocations(siteId);
  const productsQuery = useActiveProducts();
  const completeCount = useCompleteStockCount(countId);

  const locationsById = new Map((locationsQuery.data?.items ?? []).map((l) => [l.id, l]));
  const productsById = new Map((productsQuery.data?.items ?? []).map((p) => [p.id, p]));

  const changedLines = lines.filter((line) => {
    if (line.countedQty === null) return false;
    const qtyVaries = Number(line.countedQty) !== Number(line.expectedQty);
    const expectedHandling = line.expectedHandlingUnits ?? null;
    const countedHandling = line.handlingUnitsCounted ?? null;
    const handlingVaries = (expectedHandling !== null || countedHandling !== null) && (expectedHandling ?? 0) !== (countedHandling ?? 0);
    return qtyVaries || handlingVaries;
  });
  // "countedQty null = never counted" — the server's /complete handler skips these
  // outright (counts.ts: `if (line.countedQty == null) continue`), so completing
  // with lines still uncounted is NOT "counted as expected", it's "left alone,
  // no variance posted". G-B: say that truthfully rather than staying silent about
  // lines a bulk "Populate from stock on hand" may have added but nobody counted.
  const uncountedLines = lines.filter((line) => line.countedQty === null);

  function close() {
    setOpened(false);
    setResult(null);
    completeCount.reset();
  }

  function handleComplete() {
    completeCount.mutate(undefined, { onSuccess: setResult });
  }

  return (
    <>
      <Button onClick={() => setOpened(true)}>Complete count</Button>
      <Modal opened={opened} onClose={close} title="Complete count" size="lg">
        {result ? (
          <Alert color="green" icon={<IconCircleCheck size={18} />} title="Completed">
            {result.adjustmentMovementIds.length > 0
              ? `${result.adjustmentMovementIds.length} adjustment movement(s) posted.`
              : 'No variances — the count was closed with no ledger movements.'}
          </Alert>
        ) : (
          <Stack>
            {changedLines.length === 0 ? (
              <Text size="sm">
                {lines.some((line) => line.countedQty !== null)
                  ? 'Every counted line matched the ledger — completing will close the count with no ADJUSTMENT movements.'
                  : 'No lines have been counted yet — completing now will close the count with no ADJUSTMENT movements.'}
              </Text>
            ) : (
              <>
                <Text fw={600} size="sm">
                  This will post {changedLines.length} adjustment{changedLines.length === 1 ? '' : 's'}:
                </Text>
                <List size="sm" spacing="xs">
                  {changedLines.map((line) => {
                    const productName = productsById.get(line.productId)?.name ?? line.productId;
                    const locationName = locationsById.get(line.locationId)?.name ?? line.locationId;
                    const qtyDelta = Number(line.countedQty) - Number(line.expectedQty);
                    const expectedHandling = line.expectedHandlingUnits ?? 0;
                    const countedHandling = line.handlingUnitsCounted ?? 0;
                    const handlingChanged = (line.expectedHandlingUnits ?? null) !== null || (line.handlingUnitsCounted ?? null) !== null
                      ? expectedHandling !== countedHandling
                      : false;
                    return (
                      <List.Item key={line.id}>
                        <Text span fw={600}>
                          {productName}
                        </Text>{' '}
                        @ {locationName}: expected {formatQty(line.expectedQty)} → counted {formatQty(line.countedQty)} (
                        {formatSignedQty(qtyDelta)})
                        {handlingChanged && (
                          <>
                            , handling units {expectedHandling} → {countedHandling}
                          </>
                        )}
                      </List.Item>
                    );
                  })}
                </List>
              </>
            )}

            {uncountedLines.length > 0 && (
              <Alert color="amber" icon={<IconAlertTriangle size={18} />} title="Uncounted lines will be skipped">
                {uncountedLines.length} line{uncountedLines.length === 1 ? '' : 's'}{' '}
                {uncountedLines.length === 1 ? 'has' : 'have'} not been counted. Completing does NOT treat them as
                "counted as expected" — they are left exactly as they are and no adjustment is posted for them.
              </Alert>
            )}

            {completeCount.isError && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                {completeCount.error instanceof ApiRequestError ? completeCount.error.message : completeCount.error?.message}
              </Alert>
            )}

            <Group justify="flex-end">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Tooltip label="Requires Operations role" disabled={canOperate}>
                <span>
                  <Button disabled={!canOperate} loading={completeCount.isPending} onClick={handleComplete}>
                    Confirm &amp; post
                  </Button>
                </span>
              </Tooltip>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
